import { EditorContent } from '@tiptap/react';
import { useRef, useState } from 'react';

import { LinkMenu } from '../menus';

import { useBlockEditor } from '@/editor/hooks/useBlockEditor';

import '@/editor/styles/index.css';

import ImageBlockMenu from '@/editor/extensions/ImageBlock/components/ImageBlockMenu';
import { ColumnsMenu } from '@/editor/extensions/MultiColumn/menus';
import { TableColumnMenu, TableRowMenu } from '@/editor/extensions/Table/menus';
import { useSidebar } from '@/editor/hooks/useSidebar';
import { TiptapCollabProvider } from '@hocuspocus/provider';
import * as Y from 'yjs';
import { ContentItemMenu } from '../menus/ContentItemMenu';
import { TextMenu } from '../menus/TextMenu';
import { Sidebar } from '../Sidebar';
import { EditorHeader } from './components/EditorHeader';

export const BlockEditor = ({
  aiToken,
  ydoc,
  provider,
  text,
}: {
  aiToken?: string;
  ydoc: Y.Doc | null;
  provider?: TiptapCollabProvider | null | undefined;
  text?: string;
}) => {
  const menuContainerRef = useRef(null);
  const importRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);

  const leftSidebar = useSidebar();
  const { editor, users, collabState } = useBlockEditor({
    aiToken,
    ydoc,
    provider,
  });

  editor?.commands.setContent(text || '');

  const handleImportFilePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (!file || !editor) return;

    setIsLoading(true);
    editor
      .chain()
      .import({
        file,
        onImport(context) {
          if (context.error) {
            console.error(context.error);
            return;
          }
          context.setEditorContent(context.content);
          setIsLoading(false);
        },
      })
      .run();
  };

  if (!editor || !users) {
    return null;
  }

  return (
    <div className="flex h-full max-h-auto" ref={menuContainerRef}>
      <Sidebar
        isOpen={leftSidebar.isOpen}
        onClose={leftSidebar.close}
        editor={editor}
      />
      <div className="relative flex flex-col flex-1 h-full overflow-y-auto">
        <EditorHeader
          editor={editor}
          collabState={collabState}
          users={users}
          isSidebarOpen={leftSidebar.isOpen}
          toggleSidebar={leftSidebar.toggle}
          handleImportFilePick={handleImportFilePick}
          fileProcessing={isLoading}
        ></EditorHeader>

        <EditorContent editor={editor} className="flex-1 overflow-y-auto" />
        <ContentItemMenu editor={editor} />
        <LinkMenu editor={editor} appendTo={menuContainerRef} />
        <TextMenu editor={editor} />
        <ColumnsMenu editor={editor} appendTo={menuContainerRef} />
        <TableRowMenu editor={editor} appendTo={menuContainerRef} />
        <TableColumnMenu editor={editor} appendTo={menuContainerRef} />
        <ImageBlockMenu editor={editor} appendTo={menuContainerRef} />
      </div>
    </div>
  );
};

export default BlockEditor;
